<template>
  <el-popover
    trigger="click"
    placement="bottom-end"
    width="800"
    popper-class="date-filter-popper-class"
    v-model="visible"
  >
    <div class="container">
      <vc-date-picker
        class="date-picker"
        mode="range"
        is-inline
        title-position="left"
        v-model="range"
        :columns="2"
        @input="datePicked"
        @drag="selection = 'custom'"
        :max-date="new Date()"
      />
      <div class="aside">
        <div class="header">
          <h3>
            {{ $t('DateFilter_Sidebar_Filter') }}
          </h3>
        </div>
        <el-row>
          <el-col>
            <p>{{ $t('DateFilter_DateRange') }}</p>
          </el-col>
          <el-col>
            <el-select v-model="selection" @change="onChange" size="large">
              <el-option
                v-for="(period, key) in dateFilterPeriods"
                :key="key"
                :value="key"
                :label="$t(period.label)"
              >
                {{ $t(period.label) }}
              </el-option>
            </el-select>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="12">
            <p>{{ $t('DateFilter_Sidebar_From') }}</p>
          </el-col>
          <el-col :span="12">
            <p>{{ $t('DateFilter_Sidebar_To') }}</p>
          </el-col>
        </el-row>
        <el-form :model="form" :rules="rules" ref="dateForm">
          <el-row>
            <el-col class="from-date" :span="12">
              <el-form-item class="date-input" prop="fromDate">
                <el-input :placeholder="placeholder" v-model="form.fromDate" />
              </el-form-item>
            </el-col>
            <el-col class="to-date" :span="12">
              <el-form-item class="date-input" prop="toDate">
                <el-input :placeholder="placeholder" v-model="form.toDate" />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-button type="primary" @click="submitInputDates">
                {{ $t('DateFilter_Button_Apply') }}
              </el-button>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </div>

    <el-button
      class="date-picker-button"
      type="text"
      slot="reference"
      :disabled="disabled"
    >
      <span class="dropdown">
        <icon class="icon" glyph="datepicker-calendar" />
        <span class="button-text-container">
          <span>
            {{ formatDate(range.start) }}
            –
            {{ formatDate(range.end) }}
          </span>
        </span>
        <icon class="icon" glyph="chevron-down" />
      </span>
    </el-button>
  </el-popover>
</template>

<script>
import { EventBus } from '@/core/EventBus'

import moment from 'moment'
import i18n from '@/core/utils/I18n'
import {
  dateFilterPeriods,
  dateFilterMonths
} from '@/common/components/dashboard/Labels'
import { mapGetters } from 'vuex'
import { getDateFilterRange } from '@/core/helpers'

export default {
  name: 'DateFilter',
  props: {
    sessionKey: {
      type: String,
      default: null
    },
    useUserConfig: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  created() {
    if (this.userConfig.dateFilter && this.userConfig.dateFilter.period) {
      this.selection = this.userConfig.dateFilter.period
    }
    this.range = getDateFilterRange(this.getSessionKey)
    if (window.sessionStorage.getItem(this.getSessionKey)) {
      this.selection = 'custom'
    }
  },

  data() {
    const validateFromDate = (rule, value, callback) => {
      if (!this.fromDate.isValid()) {
        callback(new Error(this.$t('DateFilter_InvalidDate')))
      } else {
        if (this.form.fromDate !== '' && this.toDate.isValid()) {
          if (this.fromDate.isAfter(this.toDate)) {
            callback(new Error(this.$t('DateFilter_BeforeToDate')))
          }
        }
        callback()
      }
    }
    const validateToDate = (rule, value, callback) => {
      if (!this.toDate.isValid()) {
        callback(new Error(this.$t('DateFilter_InvalidDate')))
      } else {
        if (this.form.toDate !== '' && this.fromDate.isValid()) {
          if (this.toDate.isBefore(this.fromDate)) {
            callback(new Error(this.$t('DateFilter_AfterFromDate')))
          }
        }
        callback()
      }
    }
    return {
      dateFilterPeriods,
      dateFilterMonths,
      visible: null,
      range: { start: new Date(), end: new Date() },
      selection: 'today',
      form: { fromDate: '', toDate: '' },
      rules: {
        fromDate: [{ validator: validateFromDate, trigger: 'change' }],
        toDate: [{ validator: validateToDate, trigger: 'change' }]
      },
      skippedFirstChange: false
    }
  },

  computed: {
    ...mapGetters(['userConfig', 'userUid']),

    getSessionKey() {
      return this.sessionKey || `config.${this.userUid}.dateRange`
    },
    dateFormat() {
      // set locale to en-GB to display the default date in dd/mm/yyyy format
      const locale = i18n.locale === 'en' ? 'en-GB' : i18n.locale
      return moment.localeData(locale).longDateFormat('L')
    },
    placeholder() {
      return moment().format(this.dateFormat)
    },
    fromDate() {
      return moment(this.form.fromDate, this.dateFormat, true)
    },
    toDate() {
      return moment(this.form.toDate, this.dateFormat, true)
    }
  },

  methods: {
    submitInputDates() {
      this.$refs['dateForm'].validate(valid => {
        if (valid) {
          this.range = Object.assign(
            {},
            {
              start: moment(this.form.fromDate, this.dateFormat).toDate(),
              end: moment(this.form.toDate, this.dateFormat).toDate()
            }
          )
          this.selection = 'custom'
        } else {
          return false
        }
      })
    },

    onChange() {
      if (this.selection === 'custom') return
      this.range = this.selectionToRange(this.selection)
      this.hide()
      if (this.useUserConfig) {
        // Store preset values (last7days, last30days, etc) in userConfig
        window.sessionStorage.removeItem(this.getSessionKey)
        this.$store.dispatch('saveUserPreferences', {
          dateFilter: { period: this.selection }
        })
      }
    },

    datePicked() {
      this.form.fromDate = moment(this.range.start).format(this.dateFormat)
      this.form.toDate = moment(this.range.end).format(this.dateFormat)

      if (this.skippedFirstChange) {
        EventBus.$emit(
          'common/components/dashboard/DateFilter:onChange',
          this.range
        )
        this.$emit('change', this.range)
      }
      this.skippedFirstChange = true

      this.hide()
      if (this.selection === 'custom' || !this.useUserConfig) {
        window.sessionStorage.setItem(
          this.getSessionKey,
          JSON.stringify(this.range)
        )
      }
    },

    hide() {
      this.visible = false
    },

    formatDate(date) {
      return moment(date).format('MMM D, YYYY')
    },
    selectionToRange(selection) {
      let diff = 0
      if (dateFilterPeriods.hasOwnProperty(selection)) {
        if (dateFilterPeriods[selection].value > 0) {
          const val = dateFilterPeriods[selection].value
          diff = val ? val - 1 : val
        }

        return {
          start: moment()
            .subtract(diff, 'days')
            .startOf('day')
            .toDate(),
          // end is always the end of the current day
          end: moment()
            .endOf('day')
            .toDate()
        }
      }
    }
  }
}
</script>

<style>
.date-filter-popper-class {
  padding: 0;
  height: 270px;
}

.vc-bg-blue-200 {
  background: #e6f3fb;
}

.vc-bg-blue-600 {
  background: #1d87c8;
}
</style>

<style scoped>
.el-row {
  margin: 0 !important;
}

.dropdown {
  border: 1px solid #1d87c8;
  box-sizing: border-box;
  border-radius: 4px;
  display: flex;
  background: #ffffff;
  height: 40px;
}

.icon {
  color: #1d87c8;
  height: 20px;
  width: 20px;
  margin: 8px 12px;
}

.button-text-container span {
  font-weight: normal;
  font-size: 14px;
  line-height: 22px;
  color: #333333;
}

.aside {
  background-color: #fafafa;
}

.aside .header {
  height: 50px;
  box-shadow: inset 0px -1px 0px #f2f2f2, inset 1px 0px 0px #f2f2f2;
  display: flex;
  align-items: center;
  padding-left: 15px;
}

.header h3 {
  font-size: 14px;
  line-height: 22px;
  color: #333333;
}

.aside p {
  line-height: 22px;
  color: #808080;
  margin: 12px 0 6px;
}

.container {
  margin: 0;
  height: 100%;
  display: flex;
}

.from-date {
  padding-right: 4px !important;
}

.to-date {
  padding-left: 4px !important;
}

.date-input {
  margin-bottom: 12px;
}

.button-text-container {
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

.date-picker {
  border-style: none;
}

.date-picker-button {
  padding-top: 0;
  padding-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/common/components/dashboard/DateFilter.vue